import type { ReactNode } from 'react'
import { useId } from 'react'
import { SearchIcon } from './icons'
import styles from './FilterBar.module.css'

/** Row of labelled filter controls. Compose with FilterSearch / FilterSelect / FilterCheckbox. */
export function FilterBar({ label = 'Filters', children, actions }: { label?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <div role="group" aria-label={label} className={styles.bar}>
      <div className={styles.controls}>{children}</div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  )
}

export function FilterSearch({ label, value, onChange, placeholder, width = 280 }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; width?: number
}) {
  return (
    <label className={styles.search} style={{ width }}>
      <SearchIcon size={15} />
      <span className="sr-only">{label}</span>
      <input type="search" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    </label>
  )
}

export interface SelectOption { value: string; label: string }

export function FilterSelect({ label, value, onChange, options, showLabel = false }: {
  label: string; value: string; onChange: (v: string) => void; options: SelectOption[]; showLabel?: boolean
}) {
  const id = useId()
  return (
    <span className={styles.field}>
      <label htmlFor={id} className={showLabel ? styles.visibleLabel : 'sr-only'}>{label}</label>
      <select id={id} className={styles.select} value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </span>
  )
}

export function FilterCheckbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className={styles.check}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  )
}
